import {GridStore} from "controls/grid/gridStore";
import {MobxManager} from "framework/mobx-integration";
import {makeAutoObservable} from "mobx";
import {apiFetch} from "framework/api";
import {
	getDataSourceData, getDataSourceDataFiltersConfiguration,
	getDataSourceFiltersConfiguration,
	GetDataSourceFiltersConfigurationArgs
} from "areas/data-sources/api";
import {i18n} from "core/localization";
import {RemoteDataProvider} from "controls/grid/remoteDataProvider";
import {TimePeriodType} from "controls/react/form/timePeriodType";
import {GridConfiguration, GridSelectionType} from "controls/grid/gridConfiguration";
import {RulesConfiguration} from "controls/queryBuilder/ruleDefinition";
import {GridColumnConfig} from "controls/grid/gridColumnConfig";
import moment from "moment/moment";
import {ApplicationState} from "framework/applicationState";
import {isOneOf} from "tools/utils";

export type DataSourceData = Record<string, any>

type DataSourceDataGridStoreCustomConfigurationValues = {
	userSettingsKey?: string
}

export type DataSourceDataGridProps = GetDataSourceFiltersConfigurationArgs & {
	decimalsNumber?: number
}

export class DataSourceDataGridStore {
	gridStore: GridStore<DataSourceData>
	mobx = new MobxManager()

	props: DataSourceDataGridProps

	constructor() {
		makeAutoObservable(this)
	}

	async getDefaultGridConfig(customValues?: DataSourceDataGridStoreCustomConfigurationValues){
		const filtersResponse = await apiFetch(this.getDefaultFilters())

		if(!filtersResponse.success){
			return null
		}

		return {
			columns: this.getColumns(filtersResponse.data),
			dataProvider: new RemoteDataProvider({
				dataSource: getDataSourceData(this.props),
				filtersConfiguration: filtersResponse.data
			}),
			defaults: {
				payload: {
					timePeriod: {
						period: TimePeriodType.Last24Hours
					}
				}
			},
			selection: GridSelectionType.Many
		} as GridConfiguration<DataSourceData>
	}

	initStore(config: GridConfiguration<DataSourceData>){
		this.gridStore = new GridStore<DataSourceData>(config)
	}

	getDefaultFilters(){
		return getDataSourceDataFiltersConfiguration(this.props)
	}

	getColumns(rules: RulesConfiguration) {
		return Object.keys(rules).filter(x => x != 'id').map(columnName => {
			let column: GridColumnConfig<any> = {
				field: columnName,
				title: rules[columnName].label != 'rowHeader' ? rules[columnName].label : '',
				renderer: item => {
					if (rules[columnName].type == 'integer') {
						return item[columnName]?.toFixed(this.props.decimalsNumber ?? 0) ?? '';
					}
					if (rules[columnName].type == 'date' && !!item[columnName]) {
						return moment(item[columnName]).format(ApplicationState.momentDateTimeFormat);
					}
					if (rules[columnName].type == 'boolean') {
						if (isOneOf(item[columnName]?.toString().toLowerCase?.(), [true, 'true', 'yes'])) {
							return i18n('Yes')
						} else {
							return i18n('No')
						}
					}
					return item[columnName] ?? '';
				}
			}

			return column
		})
	}

	destroy() {
		this.gridStore?.destroy()
		this.mobx.destroy();
	}
}
